// Shared helpers used by every OrgChart view.
//
// The five views (Outline, Tree, Radial, Treemap, Network) all consume
// the same node shape returned by /api/admin/org-chart:
//   { id, name|full_name, role, title|designation, department,
//     reports|children: [...] }
//
// Keeping the role palette + the tree walkers in one place avoids
// drift between views.

export const ROLE_COLOR = {
  admin:      "#c084fc",
  manager:    "#60a5fa",
  staff:      "#00ff90",
  executive:  "#fbbf24",
  superadmin: "#f472b6",
};

export const FALLBACK_COLOR = "#00ff90";

export const colorFor = (role) =>
  ROLE_COLOR[(role || "staff").toLowerCase()] || FALLBACK_COLOR;

export const childrenOf = (node) => node?.reports || node?.children || [];

export const nameOf = (node) => node?.name || node?.full_name || "Unknown";

// Count nodes in a (sub)tree. Treats the input as a forest if given an
// array of roots.
export function countNodes(input) {
  let n = 0;
  const walk = (nodes) => nodes.forEach((node) => {
    n += 1;
    walk(childrenOf(node));
  });
  walk(Array.isArray(input) ? input : [input]);
  return n;
}

// True if the node or any descendant matches the search query.
// Empty `q` always returns true.
export function nodeMatches(node, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  const fields = [
    nameOf(node),
    node?.email,
    node?.department,
    node?.title,
    node?.designation,
    node?.role,
  ];
  if (fields.some((f) => String(f || "").toLowerCase().includes(needle))) return true;
  return childrenOf(node).some((c) => nodeMatches(c, q));
}

// Wrap a forest in a single virtual root for layout algos that need one.
// `label` controls the synthetic root's display name.
export function withSyntheticRoot(forest, label = "Organisation") {
  if (!Array.isArray(forest)) return forest;
  if (forest.length === 1) return forest[0];
  return {
    id: "__virtual_root__",
    name: label,
    role: "superadmin",
    title: "",
    department: "",
    __synthetic: true,
    reports: forest,
  };
}

// Initial of the first word — used by avatar bubbles in several views.
export const initialOf = (node) => String(nameOf(node)).trim()[0]?.toUpperCase() || "?";
